import * as PIXI from "pixi.js";
import { isScreenRowOpen } from "../../RGS/Providers/PopiPlay/holdAndWinState";
import {
  // toDataRow, (the zone test is isScreenRowOpen since 2026-10-14)
  waitUntil,
  flyTrail,
  countUp,
  // revealHiddenJackpots,
  revealBeforeCollect,
  flipJackpotsAtOnce,
} from "./chestCollection";
// NOT USED since the flying value takes its sizes from the coin and the collector:
// import { Settings } from "../../settings";
import { formatToCustomLocale } from "../../utilities";
import { createCoinLabel, setCoinLabel } from "./coinLabel";
import { playScarabReveal } from "./scarabReveal";
import { paced, trailGapMs } from "./holdAndWinPace";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";

/**
 * endCollector — the end of Hold & Win (GDD "End of Hold & Win Feature
 * (Collector)"), up to the Total Win popup.
 *
 *   0  a breath: the SAME wait the loop takes before another respin
 *      (RESPIN_NEXT_DELAY_MS), so the player sees the finished board
 *   1  the respin counter goes out (counter_transform_N), IN FULL
 *   2  then the collector comes in, in the same spot (collector_in, in full)
 *      and settles into idle — collecting starts from there
 *   3  every valued cell in the active zone, in reading order — left to
 *      right, top to bottom — sends its fire trail into the collector,
 *      CARRYING its value (Pot Collection's value traveller: the number rides
 *      the trail's head); on arrival the collector plays `collector_hit`, its
 *      total counts up by that value, and the coin goes dark (`darken`)
 *   4  `collector_final_hit`, on the SERVER's paid amount (outcome.win),
 *      played IN FULL before the Total Win follows
 *
 * Each step waits for the previous animation's own complete callback — no
 * timers stacked on top of the spines (Vladimir, 2026-09-29: "let the player
 * have some time to breathe, to see the entire board").
 *
 * A press fires the remaining trails together — Pot Collection's slam stop.
 * The count never passes the paid amount (GDD: a capped round must not show
 * an uncapped total and count back).
 *
 * The Scarab reveal (GDD step 2) runs first — scarabReveal.js — and the
 * collection then collects the MULTIPLIED numbers it leaves on the board.
 * // NOT YET: the Royal award (GDD step 6) — rare, never drawn on staging. The
 * // final snap to the paid amount already includes it.
 * // The Royal award (GDD step 6, 2026-10-01): after the coins, its own popup
 * // (`playRoyal`), then the collector counts it in — unless the coins already
 * // reached a capped payout.
 * The Royal (raw GDD End Collector step 6: "Include any awarded ROYAL JACKPOT
 * through a separate award animation"): after the coins, its value flies off
 * the Royal plate into the collector, which counts it in — unless the coins
 * already reached a capped payout. Its POPUP played earlier, on the respin
 * that filled the grid (GDD "Royal Award": "on the step that records it").
 *
 * Every delay runs on the ticker — never window timers.
 */

// The collector's number counting up to each arrival, ms (as the chests).
const COUNT_MS = 150;
// ── The flying number fades out on its way in (Vladimir, 2026-10-13: "so close
// to the end the number kinda disappears so we only see the collector") ──
// Full until this share of the head's travel, then a smooth fade to 0 as it
// reaches the collector — it never lands on top of the collector's total.
const CARRY_FADE_FROM = 0.7;
const carryAlpha = (p) => {
  if (p <= CARRY_FADE_FROM) return 1;
  const k = Math.min(1, (p - CARRY_FADE_FROM) / (1 - CARRY_FADE_FROM));
  return 1 - k * k * (3 - 2 * k);
};
// The collector counting the Royal in — a bigger number, a longer count.
const ROYAL_COUNT_MS = 600;
// A capped round's leftovers going dark: `darken_max` is 1 s, waited out
// before Max Win — in full, never cut (2026-11-03).
const DARKEN_MAX_MS = 1000;
// ...and the breath BEFORE it (2026-11-03: "it happens quite fast"): the
// collection and its final hit are over and seen, then the leftovers dim.
// const DARKEN_MAX_GAP_MS = 800;
// const DARKEN_MAX_GAP_MS = 500; // a bit shorter (2026-11-03)
// (No gap at all — PO, 2026-11-03.)
// ── The counter -> collector handover ──
// The counter's out (counter_transform_N) is a 0.167 s shrink to nothing and
// the collector's in pops from nothing straight after — back to back it read
// as abrupt (Vladimir, 2026-09-29). The out plays at half speed (0.33 s) and
// the collector waits a beat once the counter is gone.
const COUNTER_OUT_TIME_SCALE = 0.5;
const COUNTER_TO_COLLECTOR_MS = 200;

/**
 * @param {object}   p
 * @param {Array}    p.values        the final grid, screen rows
 * @param {number}   p.unlockedRows  active rows (data rows 0..n-1)
 * @param {number}   p.win           what the round paid (subunits, capped)
 * @param {number}   p.settleDelayMs the wait before it all starts — the
 *                                   loop's own RESPIN_NEXT_DELAY_MS
 * @param {object}   p.overlay       HoldAndWinOverlay's handle
 * @param {object}   [p.trailLayer]  where the trails fly — above the whole
 *                                   room (the controller's trailLayer)
 * @param {object}   p.counter       HoldAndWinCounterController
 * @param {object}   p.collector     HoldAndWinCollectorController
 * @param {Function} p.isSkipped     true once the player pressed to skip
 * @returns {Promise<void>}
 */
export const playEndCollector = async ({
  values,
  unlockedRows,
  win,
  settleDelayMs = 0,
  overlay,
  trailLayer,
  counter,
  collector,
  isSkipped,
  // The settling step's scarabs (reveal order) and the server's product grid.
  scarabs = null,
  scarabMultipliers = null,
  // Clears the press flag between the reveal and the collection, so a press
  // that skipped the reveal does not skip the collection too.
  resetSkip = null,
  // ── The round hit the MAX-WIN cap (2026-10-01) ── Pot Collection's rule
  // (PotCollectionEndOfBonusEngine): the collection STOPS on the coin that
  // reaches the cap — `win` is the capped payout — and the rest stay on the
  // board. The round then shows Max Win instead of Total Win (the caller).
  capped = false,
  // ── The Royal (GDD step 6) ── its amount (subunits, 0 when not awarded)
  // // and the popup to await before the collector takes it.
  // and the Royal plate's value text it flies from (null: counted in place).
  royal = 0,
  // playRoyal = null,
  royalSource = null,
  // The box zooms out to the collector's fit before the counter goes
  // (HoldAndWinGameViewController.playReelZoomOut, 2026-10-14).
  zoomOut = null,
}) => {
  // 0: the finished board, for as long as the next respin would have waited.
  await counter?.waitForIdle?.();
  if (settleDelayMs > 0) await waitUntil(() => false, settleDelayMs);
  // 0b: the Scarabs reveal and multiply their neighbours (GDD step 2) — on
  // the board, before the collector comes in. What they leave is collected.
  // Every chest has done its purpose by now — any still open (a reload, or a
  // golden chest that did not collect on the last respin) shuts first.
  overlay?.closeOpenChests?.();
  // eslint-disable-next-line no-param-reassign
  values = await playScarabReveal({
    scarabs,
    values,
    scarabMultipliers,
    unlockedRows,
    overlay,
    isSkipped,
  });
  resetSkip?.();
  // 0c: the box zooms out, so the collector comes in where it was fitted.
  await zoomOut?.();
  // 1-2: the counter out IN FULL, then the collector in IN FULL (it settles
  // into idle by itself) — each on its own complete callback.
  await counter?.playOut?.(COUNTER_OUT_TIME_SCALE);
  // await waitUntil(() => false, COUNTER_TO_COLLECTOR_MS);
  // At the respin's pace — turbo's is shorter (holdAndWinPace, 2026-10-23).
  await waitUntil(() => false, paced(COUNTER_TO_COLLECTOR_MS));
  await collector?.show(0);

  // 3: the sources — every valued cell in the zone, left to right, top to
  // bottom (GDD "Collection Animation").
  const sources = [];
  if (values) {
    for (let r = 0; r < values[0].length; r++) {
      for (let c = 0; c < values.length; c++) {
        const v = values[c]?.[r];
        // if (v > 0 && toDataRow(r) < unlockedRows) sources.push({ c, r, v });
        if (v > 0 && isScreenRowOpen(r, unlockedRows)) sources.push({ c, r, v });
      }
    }
  }
  // // Jackpot coins turn into their values first (revealHiddenJackpots).
  // await revealHiddenJackpots(overlay, sources, isSkipped);
  // — no: each turns on ITS turn (revealBeforeCollect, in flyFrom).
  // ── ...all at once again, before the first trail (2026-10-06) ──
  // flipJackpotsAtOnce; revealBeforeCollect in flyFrom is then a no-op.
  await flipJackpotsAtOnce(overlay, sources);

  const target = collector?.anchor;
  const layer = {
    trailContainer: trailLayer ?? overlay?.trailLayer?.(),
    // Read as each trail leaves — the current cell size after a resize.
    get symbolHeight() {
      return overlay?.cellHeight?.();
    },
  };
  let total = 0;
  // The cells the trails took ("col_row") — a capped round dims the rest.
  const collected = new Set();

  if (target && layer.trailContainer && sources.length) {
    // Where the collector is as each trail leaves (a resize moves it).
    const targetGlobal = () => target.toGlobal(new PIXI.Point(0, 0));
    // ── Pot Collection's arrival, as the chests already do ── the count runs
    // but the next trail does not wait for it (executeCollect: _animateCounter
    // per arrival, _waitForCounters once at the end). Awaiting 150 ms per coin
    // made the collection drag (Vladimir, 2026-09-29: "bam bam and thats it").
    const counters = [];
    const arrive = (v) => {
      const before = total;
      total = Math.min(total + v, win > 0 ? win : total + v);
      collector.hit();
      // Its sound (2026-10-22, the designer's collector) — once per frame, so
      // a skipped burst arriving together is one hit, not a stack.
      soundManager.playOncePerFrame(SOUND_NAME.collector_hit);
      // counters.push(countUp(before, total, COUNT_MS, (n) => collector.setValue(n)));
      counters.push(countUp(before, total, paced(COUNT_MS), (n) => collector.setValue(n)));
    };
    // ── The flying number GROWS from the coin's number to the collector's ──
    //
    // EXACT at both ends, by construction:
    //   the texture  drawn at the collector total's own font size and style,
    //                so the label cache hands it the SAME texture the total
    //                uses — no rescaled pixels at the end
    //   the start    the coin's number as it stands on screen, read off the
    //                coin's own value sprite the moment it leaves
    //   the end      the total's scale on screen, read LIVE every frame (the
    //                collector's in/hit clips and a resize move it)
    // It grows with the head's progress, so it lands exactly the size of the
    // number it joins.
    const layerScale = () => {
      const wt = layer.trailContainer.worldTransform;
      return Math.hypot(wt.c, wt.d) || 1;
    };
    const worldScaleY = (obj) => {
      const wt = obj?.worldTransform;
      return wt ? Math.hypot(wt.c, wt.d) : 0;
    };
    // const flyFrom = (src) => {
    // `light`: one of a skipped burst — the LIGHT trail.
    const flyFrom = async (src, light = false) => {
      // A jackpot coin turns into its value first — on its own turn.
      await revealBeforeCollect(overlay, src, isSkipped);
      const from = overlay.cellAnchor(src.c, src.r);
      const text = formatToCustomLocale(src.v);

      // The coin's number on screen, px (its sprite's texture height is its
      // font's; its world scale is everything scaling it).
      const coinLabel = overlay.valueLabel(src.c, src.r);
      const coinPx = (coinLabel?._fontSize || 0) * worldScaleY(coinLabel);

      const carried = createCoinLabel();
      // setCoinLabel(carried, text, collector.fontSize, 0);
      // In the coin's own colours (a jackpot coin's tier).
      setCoinLabel(carried, text, collector.fontSize, 0, overlay.valueStyle?.(src.c, src.r));
      const startScale = coinPx > 0 ? coinPx / (collector.fontSize * layerScale()) : 0;
      carried.scale.set(startScale);

      // The value MOVES: it leaves the coin the moment the trail fires and
      // rides the trail's head into the collector. The coin keeps nothing —
      // it plays `darken` and stays in its last frame.
      // overlay.setValue(src.c, src.r, 0);
      // overlay.darken(src.c, src.r);
      // ── ...unless it gives only PART (a capped round, 2026-11-03) ── the
      // cell that crosses the cap sends just what reaches it and keeps the
      // rest on its number; the leftovers' dim takes it after (step 5).
      if (src.keep > 0) {
        overlay.setValue(src.c, src.r, src.keep);
      } else {
        overlay.setValue(src.c, src.r, 0);
        overlay.darken(src.c, src.r);
      }

      const grow = (p) => {
        if (carried.destroyed) return;
        const endScale = collector.textWorldScale() / layerScale();
        carried.scale.set(startScale + (endScale - startScale) * p);
        carried.alpha = carryAlpha(p);
      };
      return flyTrail(layer, from, targetGlobal, carried, grow, light).then(() => arrive(src.v));
    };

    // Capped: only the coins up to (and including) the one that reaches the
    // cap fly — the collector's total is already held to `win` (arrive).
    let flying = sources;
    if (capped && win > 0) {
      let planned = 0;
      const last = sources.findIndex(({ v }) => {
        planned += v;
        return planned >= win;
      });
      if (last > -1) flying = sources.slice(0, last + 1);
      // ── The last one gives only what reaches the cap (2026-11-03) ── its
      // trail carries `win - (the rest before it)`; it keeps the remainder.
      if (last > -1) {
        const before = flying.slice(0, -1).reduce((sum, { v }) => sum + v, 0);
        const lastSrc = flying[flying.length - 1];
        const take = win - before;
        if (take > 0 && take < lastSrc.v) {
          flying = [...flying.slice(0, -1), { ...lastSrc, v: take, keep: lastSrc.v - take }];
        }
      }
    }
    // flying.forEach(({ c, r }) => collected.add(`${c}_${r}`));
    // A cell that kept a remainder is NOT collected — it dims with the rest.
    flying.forEach(({ c, r, keep }) => {
      if (!(keep > 0)) collected.add(`${c}_${r}`);
    });
    // for (let i = 0; i < sources.length; i++) {
    //   if (isSkipped()) {
    //     await Promise.all(sources.slice(i).map(flyFrom));
    //     break;
    //   }
    //   await flyFrom(sources[i]);
    // }
    const gap = trailGapMs();
    const flights = [];
    for (let i = 0; i < flying.length; i++) {
      if (isSkipped()) {
        // The rest together on LIGHT trails — up to 40 full trails at once
        // lagged a desktop (Vladimir, 2026-10-01: "we have to make trails
        // lightweight").
        // await Promise.all(flying.slice(i).map(flyFrom));
        // Every jackpot coin still on its tier word turns FIRST, all at once
        // (Pot Collection's fast mode: flips awaited, then the trails) —
        // so the burst never collects a coin that did not show its value.
        await Promise.all(
          flying.slice(i).map((src) => revealBeforeCollect(overlay, src, isSkipped)),
        );
        await Promise.all(flying.slice(i).map((src) => flyFrom(src, true)));
        break;
      }
      // await flyFrom(flying[i]);
      // ── Turbo: one by one, NOT waiting for each other (2026-10-23) ── each
      // trail leaves TRAIL_GAP_MS after the one before, at its own speed.
      if (gap == null) {
        await flyFrom(flying[i]);
      } else {
        flights.push(flyFrom(flying[i]));
        if (i < flying.length - 1) await waitUntil(isSkipped, gap);
      }
    }
    await Promise.all(flights);
    // The last count finishes before the collector lands on the paid amount.
    await Promise.all(counters);
  }

  // 6 (GDD): the Royal — its popup, then the collector counts it in. Not
  // when the coins already reached a capped payout: nothing is left to add.
  // if (royal > 0 && (win <= 0 || total < win)) {
  //   await playRoyal?.();
  //   const before = total;
  //   total = win > 0 ? Math.min(total + royal, win) : total + royal;
  //   collector?.hit();
  //   await countUp(before, total, ROYAL_COUNT_MS, (n) => collector?.setValue(n));
  // }
  // The award animation: the coins' own trail, from the Royal plate, carrying
  // the Royal's number in the plates' golden style (Pot Collection flies its
  // Grand Jackpot from its label into the collector the same way).
  if (royal > 0 && (win <= 0 || total < win)) {
    const trailContainer = trailLayer ?? overlay?.trailLayer?.();
    const target = collector?.anchor;
    if (royalSource && target && trailContainer && !isSkipped()) {
      const layer = { trailContainer, symbolHeight: overlay?.cellHeight?.() };
      const wt = trailContainer.worldTransform;
      const layerScale = Math.hypot(wt.c, wt.d) || 1;
      const swt = royalSource.worldTransform;
      const sourcePx = (royalSource._fontSize || royalSource.style?.fontSize || 0) *
        (swt ? Math.hypot(swt.c, swt.d) : 0);
      const carried = createCoinLabel();
      setCoinLabel(carried, formatToCustomLocale(royal), collector.fontSize, 0, "jackpots");
      const startScale = sourcePx > 0 ? sourcePx / (collector.fontSize * layerScale) : 0;
      carried.scale.set(startScale);
      const grow = (p) => {
        if (carried.destroyed) return;
        const endScale = collector.textWorldScale() / layerScale;
        carried.scale.set(startScale + (endScale - startScale) * p);
        carried.alpha = carryAlpha(p);
      };
      // From the plate's number itself (anchored centre), like a coin's cell.
      await flyTrail(
        layer,
        royalSource,
        target.toGlobal(new PIXI.Point(0, 0)),
        carried,
        grow,
      );
    }
    const before = total;
    total = win > 0 ? Math.min(total + royal, win) : total + royal;
    collector?.hit();
    soundManager.playOncePerFrame(SOUND_NAME.collector_hit);
    // await countUp(before, total, ROYAL_COUNT_MS, (n) => collector?.setValue(n));
    await countUp(before, total, paced(ROYAL_COUNT_MS), (n) => collector?.setValue(n));
  }

  // 4: the server's number, always — and the final hit played in full.
  collector?.setValue(win);
  // Its sound (2026-10-22, the designer's collector_last) — the last coin is
  // in, the total is final.
  soundManager.play(SOUND_NAME.collector_last);
  await collector?.finalHit();

  // 5: a CAPPED round (PO, 2026-11-03): every symbol left uncollected loses
  // its number and goes darker than a collected one, all at once — then the
  // caller shows Max Win.
  if (capped) {
    // await waitUntil(isSkipped, paced(DARKEN_MAX_GAP_MS));
    // Not cut by a press: the gap is there to SEE what was collected.
    // await waitUntil(() => false, paced(DARKEN_MAX_GAP_MS));
    overlay?.darkenUncollected?.(collected);
    // await waitUntil(isSkipped, paced(DARKEN_MAX_MS));
    // ALWAYS in full, a press or a faster pace included (2026-11-03: Max Win
    // came up over a skipped dim) — the clip itself plays its whole second.
    await waitUntil(() => false, DARKEN_MAX_MS);
  }
};
